<template>
  <div class="min-h-screen bg-[var(--cream)]">
    <section class="pt-24 pb-16 px-6">
      <div class="max-w-6xl mx-auto">
        <!-- Service Header -->
        <div class="text-center mb-12 fade-in-content">
          <h1 class="text-5xl md:text-6xl font-bold text-[var(--forest)] italic mb-4">
            {{ serviceData.title }}
          </h1>
          <p class="text-xl text-[var(--moss)] italic max-w-3xl mx-auto leading-relaxed">
            {{ serviceData.subtitle }}
          </p>
        </div>

        <!-- Hero Image -->
        <div
          class="relative w-full aspect-[16/9] md:aspect-[21/9] rounded-[40%_60%_30%_70%/60%_30%_70%_40%] overflow-hidden shadow-2xl border-4 md:border-8 border-white mb-16 service-hero-blob"
        >
          <div
            class="absolute inset-0 bg-cover bg-center"
            :style="{ backgroundImage: `url('${serviceData.heroImage}')` }"
          ></div>
          <div class="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        </div>

        <!-- Main Content -->
        <div class="grid lg:grid-cols-3 gap-12 mb-16">
          <!-- Main Content Column -->
          <div class="lg:col-span-2 space-y-8">
            <!-- Overview -->
            <div class="fade-in-item">
              <h2 class="text-3xl font-bold text-[var(--forest)] italic mb-4">Overview</h2>
              <div class="space-y-4 text-[var(--moss)] italic leading-relaxed">
                <p v-for="(para, idx) in serviceData.overview" :key="idx">{{ para }}</p>
              </div>
            </div>

            <!-- Key Features -->
            <div class="fade-in-item">
              <h2 class="text-3xl font-bold text-[var(--forest)] italic mb-4">Key Features</h2>
              <ul class="space-y-3">
                <li
                  v-for="(feature, idx) in serviceData.features"
                  :key="idx"
                  class="flex items-start gap-3 text-[var(--moss)] italic"
                >
                  <span class="material-symbols-outlined text-[var(--terracotta)] mt-1">check_circle</span>
                  <span>{{ feature }}</span>
                </li>
              </ul>
            </div>

            <!-- What to Expect -->
            <div class="fade-in-item">
              <h2 class="text-3xl font-bold text-[var(--forest)] italic mb-4">What to Expect</h2>
              <div class="space-y-4 text-[var(--moss)] italic leading-relaxed">
                <p v-for="(para, idx) in serviceData.expectations" :key="idx">{{ para }}</p>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="space-y-8">
            <!-- Quick Info Card -->
            <div class="bg-white p-8 rounded-2xl shadow-lg border border-[var(--sand)] blob-card fade-in-item">
              <h3 class="text-2xl font-bold text-[var(--forest)] italic mb-4">Quick Info</h3>
              <div class="space-y-4 text-[var(--moss)]">
                <div>
                  <div class="font-semibold text-[var(--forest)] mb-1">Duration</div>
                  <div class="italic">{{ serviceData.duration }}</div>
                </div>
                <div>
                  <div class="font-semibold text-[var(--forest)] mb-1">Format</div>
                  <div class="italic">{{ serviceData.format }}</div>
                </div>
                <div>
                  <div class="font-semibold text-[var(--forest)] mb-1">Availability</div>
                  <div class="italic">{{ serviceData.availability }}</div>
                </div>
              </div>
            </div>

            <!-- CTA Card -->
            <div class="bg-[var(--forest)] p-8 rounded-2xl shadow-lg text-white fade-in-item">
              <h3 class="text-2xl font-bold italic mb-4">Get Started</h3>
              <p class="text-[var(--sage)] italic mb-6 leading-relaxed">
                Ready to begin your journey? Reach out to learn more about this service.
              </p>
              <button @click="openJoinModal" class="btn-organic bg-[var(--terracotta)] text-white px-8 py-3 font-bold w-full">
                Contact Us
              </button>
            </div>
          </div>
        </div>

        <!-- Image Gallery -->
        <div class="grid md:grid-cols-2 gap-8 mb-16">
          <div
            v-for="(image, idx) in serviceData.gallery"
            :key="idx"
            class="relative aspect-[4/3] rounded-[30%_70%_70%_30%/30%_30%_70%_70%] overflow-hidden shadow-xl border-4 border-white service-gallery-blob fade-in-item"
          >
            <div
              class="absolute inset-0 bg-cover bg-center"
              :style="{ backgroundImage: `url('${image}')` }"
            ></div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { servicesData } from '../data/servicesData'

export default {
  name: 'ServicesDetail',
  setup() {
    const route = useRoute()

    const serviceId = computed(() => route.params.id)
    const serviceData = computed(() => {
      return servicesData.find(s => s.id === serviceId.value) || servicesData[0]
    })

    const openJoinModal = () => {
      window.dispatchEvent(new CustomEvent('open-join-modal'))
    }

    // Setup fade-in animations
    const setupFadeIn = () => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in-visible')
            observer.unobserve(entry.target)
          }
        })
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      })

      document.querySelectorAll('.fade-in-content, .fade-in-item').forEach(el => {
        observer.observe(el)
      })
    }

    onMounted(() => {
      setupFadeIn()
    })

    return {
      serviceData,
      openJoinModal
    }
  }
}
</script>

<style scoped>
.service-hero-blob {
  transition: border-radius 0.5s ease-in-out;
}

.service-gallery-blob {
  transition: border-radius 0.5s ease-in-out;
}

.service-gallery-blob:hover {
  border-radius: 50% 50% 45% 55% / 60% 60% 40% 40% !important;
}

.fade-in-content,
.fade-in-item {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.fade-in-content.fade-in-visible,
.fade-in-item.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}
</style>
